import React, { useMemo } from "react";
import { Popover } from "antd";

/**
 * The ReFundDealInfo function renders a table with fund details based on the props passed to it.
 * @param props - The `props` parameter is an object that contains data passed to the `ReFundDealInfo`
 * component. It is used to access the data needed to render the component.
 * @returns The ReFundDealInfo component is returning a JSX element, which represents the structure and
 * content of the component's UI.
 */
const ReFundDealInfo = (props) => {
  // Extract and process selected fund details once
  const selectedFundDetails = useMemo(() => {
    if (!props?.data || Object.keys(props.data).length === 0) {
      return [];
    }

    const details = [];
    
    Object.entries(props.data).forEach(([key, value]) => {
      if (
        value !== "The fund detail has been viewed successfully" &&
        key !== "status" &&
        value?.length > 0
      ) {
        value.forEach((round) => {
          Object.entries(round).forEach(([, value1]) => {
            if (value1?.selected === true) {
              details.push(value1);
            }
          });
        });
      }
    });

    return details;
  }, [props.data]);

  // Helper function to render source links
  const renderSourceLinks = (source) => {
    if (!source) return "--";

    const links = source.split(";").filter((link) => link?.trim().length !== 0);
    
    return links.map((link, index) => (
      <Popover
        key={index}
        placement="topLeft"
        className="overflow text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold hover:underline"
        content={
          <a
            className="overflow text-[11pt] hover:text-[#F1CA75] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold hover:underline"
            href={link}
            target="_blank"
            rel="noreferrer"
          >
            {link}
          </a>
        }
      >
        <a
          className="overflow text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold hover:underline"
          href={link}
          target="_blank"
          rel="noreferrer"
        >
          <span>
            Link {index + 1}
            {index < links.length - 1 ? ", " : ""}
          </span>
        </a>
      </Popover>
    ));
  };

  // Helper function to render field value
  const renderFieldValue = (value, fallback = "--") => {
    return value || fallback;
  };

  // Helper function to render size value
  const renderSize = (targetSize) => {
    if (targetSize && targetSize !== "$ 0 M" && targetSize !== 0) {
      return `$ ${targetSize} M`;
    }
    return "--";
  };

  return (
    <div className="px-5">
      <p className="text-[14pt] leading-[19pt] lg:text-[17px] text-[#333333] font-semibold lg:leading-[22px]">
        Fund Details
      </p>
      <div className="overflow-x-auto scrollbar">
        <table className="w-full">
          <tbody>
            <tr className="border-b-[#E5E5E5] border-b space-x-6">
              <td className="py-1 lg:leading-[21px] font-sans_book_body">
                <p className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto">
                  Sector
                </p>
                {selectedFundDetails.map((detail, index) => (
                  <p
                    key={detail.deal_id || index}
                    className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto font-semibold"
                  >
                    {renderFieldValue(detail.sector)}
                  </p>
                ))}
              </td>
              <td className="py-1 pl-12 lg:leading-[21px] font-sans_book_body">
                <p className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto">
                  Size (US$ M)
                </p>
                {selectedFundDetails.map((detail, index) => (
                  <p
                    key={detail.deal_id || index}
                    className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto font-semibold"
                  >
                    {renderSize(detail.target_size)}
                  </p>
                ))}
              </td>
              <td className="py-1 pl-5 lg:leading-[21px] font-sans_book_body">
                <p className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto">
                  Date
                </p>
                {selectedFundDetails.map((detail, index) => (
                  <p
                    key={detail.deal_id || index}
                    className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto font-semibold"
                  >
                    {renderFieldValue(detail.date)}
                  </p>
                ))}
              </td>
            </tr>
            <tr className="border-b-[#E5E5E5] border-b font-sans_book_body">
              <td className="py-2 lg:leading-[21px]">
                <p className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto">
                  Fund Manager
                </p>
                {selectedFundDetails.map((detail, index) => (
                  <p
                    key={detail.deal_id || index}
                    className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto font-semibold"
                  >
                    {renderFieldValue(detail.fundManager)}
                  </p>
                ))}
              </td>
              <td className="py-2 pl-12 lg:leading-[21px]">
                <p className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto">
                  Fund Status
                </p>
                {selectedFundDetails.map((detail, index) => (
                  <p
                    key={detail.deal_id || index}
                    className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto font-semibold"
                  >
                    {renderFieldValue(detail.fund_status)}
                  </p>
                ))}
              </td>
              <td className="py-1 pl-5 lg:leading-[21px]">
                <p className="text-[11pt] leading-[18pt] lg:text-[14px] text-[#333333] whitespace-nowrap cursor-auto">
                  Capital Source
                </p>
                {selectedFundDetails.map((detail, index) => (
                  <p
                    key={detail.deal_id || index}
                    className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto font-semibold"
                  >
                    {renderFieldValue(detail.capital_source)}
                  </p>
                ))}
              </td>
            </tr>
            <tr>
              <td className="py-2 lg:leading-[21px] font-sans_book_body">
                <p className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] whitespace-nowrap cursor-auto">
                  Source
                </p>
                {selectedFundDetails.map((detail, index) => (
                  <p
                    key={detail.deal_id || index}
                    className="text-[11pt] leading-[18pt] lg:text-[13px] text-[#333333] cursor-auto font-semibold"
                  >
                    {renderSourceLinks(detail.source)}
                  </p>
                ))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReFundDealInfo;
